<template>
  <div class="absolute left-0 top-0 h-full w-full">
    <div class="relative flex h-full w-full flex-col gap-4 p-4 md:p-8">
      <div class="mb-6">
        <h1 class="mb-2 text-2xl font-bold text-foreground sm:text-3xl">{{ $t("settings.auto.usuarios") }}</h1>
        <p class="text-muted-foreground">{{ $t("settings.auto.gestiona_las_personas_de_la_organizacion_selecci") }}</p>
      </div>

      <div class="mb-6 flex flex-col gap-3 sm:flex-row sm:gap-4">
        <button
          v-if="!authStore.isReadOnlyArchive"
          type="button"
          class="inline-flex w-auto items-center justify-center gap-2 rounded-lg bg-egreen-500 px-4 py-1 text-md text-white"
          @click="showModal()"
        >
          <IconUsersPlus class="h-5 w-5" />
          <span>{{ $t("settings.auto.agregar") }}</span>
        </button>

        <div class="relative ml-auto flex-1 sm:max-w-xs">
          <IconSearch
            class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          />
          <input
            v-model="search"
            type="text"
            name="search"
            class="rounded-lg border-ewhite-300 px-4 py-1.5 pl-9 text-md focus:ring-blue-500 sm:w-full md:w-80"
            :placeholder="$t('settings.auto.buscar_persona_correo_dni_o_rol')"
            @keyup="applySearch()"
          />
        </div>
      </div>

      <div class="min-h-0 flex-1 overflow-y-auto">
        <div
          v-if="users_lists.length > 0"
          class="grid min-h-0 flex-1 grid-cols-1 gap-4 md:grid-cols-2"
        >
          <div
            v-for="user in users_lists"
            :key="user.id"
            data-testid="user-card"
            class="group overflow-hidden rounded-2xl border border-ewhite-200 bg-white transition-all duration-300 hover:border-orange-300 hover:shadow-xl hover:shadow-eblack-500/10"
          >
            <div class="flex h-full flex-col gap-4 p-6">
              <div class="flex items-start justify-between gap-4">
                <div class="min-w-0">
                  <h3 class="truncate text-xl font-bold text-foreground">
                    {{ user.assigned_person || "Sin nombre" }}
                  </h3>
                  <p
                    class="mt-1 flex items-center gap-2 text-sm text-muted-foreground"
                  >
                    <IconAt class="h-4 w-4 shrink-0" />
                    <span class="truncate">{{ user.email }}</span>
                  </p>
                </div>
                <span
                  class="shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium"
                  :class="membershipBadgeClass(user.membership_status)"
                >
                  {{ membershipStatusLabel(user.membership_status) }}
                </span>
              </div>

              <div
                class="grid gap-2 text-sm text-muted-foreground sm:grid-cols-2"
              >
                <div
                  class="flex items-center gap-2 rounded-lg border border-eblack-200 bg-eblack-50 px-3 py-2"
                >
                  <IconId class="h-4 w-4 shrink-0" />
                  <span>{{ user.dni || "Sin identificación" }}</span>
                </div>
                <div
                  class="flex items-center gap-2 rounded-lg border border-eblack-200 bg-eblack-50 px-3 py-2"
                >
                  <IconPhone class="h-4 w-4 shrink-0" />
                  <span>{{ formatPhone(user.contact_phone) }}</span>
                </div>
                <div
                  class="flex items-center gap-2 rounded-lg border border-eblack-200 bg-eblack-50 px-3 py-2"
                >
                  <IconShield class="h-4 w-4 shrink-0" />
                  <span>{{ roleLabel(user) }}</span>
                </div>
                <div
                  class="flex items-center gap-2 rounded-lg border border-eblack-200 bg-eblack-50 px-3 py-2"
                >
                  <IconClock class="h-4 w-4 shrink-0" />
                  <span>{{ formatDate(user.last_connections) }}</span>
                </div>
              </div>

              <div
                class="flex items-center gap-2 text-xs text-muted-foreground"
              >
                <IconBuilding class="h-4 w-4 shrink-0" />
                <span>{{
                  user.company_name || "Organización seleccionada"
                }}</span>
              </div>

              <div class="mt-auto flex justify-end gap-2">
                <button
                  v-if="canResetPasswordFor(user)"
                  data-testid="reset-password-button"
                  class="flex items-center gap-1 rounded-xl bg-eblack-700 px-4 py-1 text-sm text-white"
                  type="button"
                  @click="openPasswordReset(user)"
                >
                  <IconKey class="h-4 w-4" />
                  <span>{{ $t("settings.auto.restablecer_contrasena") }}</span>
                </button>
                <button
                  v-if="!isOwner(user) && !authStore.isReadOnlyArchive"
                  class="flex items-center gap-1 rounded-xl bg-eblue-500 px-4 py-1 text-sm text-white"
                  type="button"
                  @click="editUser(user)"
                >
                  <span>{{ $t("settings.auto.editar") }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="flex flex-col items-center justify-center py-16">
          <div
            class="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-slate-100"
          >
            <IconUsers class="h-10 w-10 text-slate-400" />
          </div>
          <p v-if="loading" class="mb-1 text-lg font-semibold text-foreground">{{ $t("settings.auto.cargando_usuarios") }}</p>
          <p v-else class="mb-1 text-lg font-semibold text-foreground">{{ $t("settings.auto.no_se_encontraron_usuarios") }}</p>
        </div>
      </div>

      <div
        id="user-action-modal"
        class="absolute right-0 top-0 h-full rounded-lg border bg-gray-100 p-2 sm:w-64 md:w-80 lg:w-1/3"
        :class="{ 'show animate-fade-in-right': openModal, hidden: !openModal }"
      >
        <FormLayout
          :user="userSelected"
          @close-modal="closeModal"
          @init-data="initData"
        />
      </div>

      <PasswordResetModal
        v-if="passwordResetUser"
        :title="$t('settings.auto.restablecer_contrasena')"
        :user-label="passwordResetUser.email"
        :help-text="$t('settings.auto.restablecer_contrasena_ayuda')"
        :new-password-label="$t('settings.auto.contrasena_nueva')"
        :confirm-password-label="$t('settings.auto.confirmar_contrasena')"
        :submit-text="$t('settings.auto.restablecer')"
        :submitting-text="$t('settings.auto.actualizando')"
        :cancel-text="$t('settings.auto.cancelar')"
        :min-length-text="$t('settings.auto.minimo_8_caracteres')"
        :mismatch-text="$t('settings.auto.las_contrasenas_no_coinciden')"
        :is-submitting="passwordResetLoading"
        :error="passwordResetError"
        @submit="resetPassword"
        @cancel="closePasswordReset"
      />
    </div>
  </div>
</template>

<script>
import {
  IconAt,
  IconBuilding,
  IconClock,
  IconId,
  IconKey,
  IconPhone,
  IconSearch,
  IconShield,
  IconUsers,
  IconUsersPlus,
} from "@tabler/icons-vue";
import FormLayout from "./components/FormLayout.vue";
import { useToast } from "vue-toastification";
import { defineRulesFor, resolvePasswordErrorKey, useAuthStore } from "@/domains/auth";
import { useOrganizationStore } from "@/domains/organization";
import { formatPhoneNumberForDisplay } from "@/shared/utils/phone-number";
import usersRequests from "@/services/admin/users-service";
import PasswordResetModal from "@/components/ui/PasswordResetModal.vue";

export default {
  name: "UsersIndex",
  data() {
    return {
      users: [],
      search: "",
      users_lists: [],
      openModal: false,
      userSelected: null,
      passwordResetUser: null,
      passwordResetLoading: false,
      passwordResetError: "",
      toast: useToast(),
      loading: false,
    };
  },
  components: {
    FormLayout,
    IconAt,
    IconBuilding,
    IconClock,
    IconId,
    IconKey,
    IconPhone,
    IconSearch,
    IconShield,
    IconUsers,
    IconUsersPlus,
    PasswordResetModal,
  },
  setup() {
    const authStore = useAuthStore();
    const organizationStore = useOrganizationStore();
    return {
      authStore,
      organizationStore,
    };
  },
  methods: {
    canResetPasswordFor(user) {
      if (this.authStore.isReadOnlyArchive || this.isCurrentUser(user)) {
        return false;
      }
      if (this.authStore.isSuperAdmin) return true;

      // Org admins cannot reset owners; the API also rejects platform owners.
      return this.isCurrentOrgAdmin && !this.isOwner(user);
    },
    isCurrentUser(user) {
      const currentUserId = this.authStore.user?.user_id;
      return !!currentUserId && String(user.id) === String(currentUserId);
    },
    formatPhone(value) {
      return formatPhoneNumberForDisplay(value) || "Sin teléfono";
    },
    editUser(user) {
      this.userSelected = user;
      this.openModal = true;
    },
    openPasswordReset(user) {
      this.passwordResetUser = user;
      this.passwordResetError = "";
    },
    closePasswordReset() {
      if (this.passwordResetLoading) return;
      this.passwordResetUser = null;
      this.passwordResetError = "";
    },
    async resetPassword(newPassword) {
      if (!this.passwordResetUser) return;
      this.passwordResetLoading = true;
      this.passwordResetError = "";
      try {
        const reset = this.authStore.isSuperAdmin
          ? usersRequests.resetPassword
          : usersRequests.resetOrgMemberPassword;
        await reset(this.passwordResetUser.id, { newPassword });
        this.toast.success(this.$t("settings.auto.contrasena_actualizada_correctamente"));
        this.closePasswordReset();
      } catch (error) {
        this.passwordResetError = this.$t(resolvePasswordErrorKey(error));
      } finally {
        this.passwordResetLoading = false;
      }
    },
    closeModal() {
      this.userSelected = null;
      const element = document.getElementById("user-action-modal");
      if (!element) {
        this.openModal = false;
        return;
      }
      element.classList.remove("animate-fade-in-right");
      element.classList.add("animate-fade-out-right");
      element.addEventListener(
        "animationend",
        () => {
          element.classList.add("hidden");
          element.classList.remove("animate-fade-out-right");
          this.openModal = false;
        },
        { once: true }
      );
    },
    showModal() {
      this.userSelected = null;
      this.openModal = true;
    },
    initData() {
      this.loading = true;
      const currentOrgId = Number(this.authStore.currentOrgId);

      if (!currentOrgId) {
        this.users = [];
        this.users_lists = [];
        this.loading = false;
        return;
      }

      this.organizationStore
        .fetchMembers(currentOrgId)
        .then((data) => {
          this.users = data.map((member) => ({
            id: member.user.id,
            ...member.user,
            role_key: member.role_key,
            role_label: member.role_label,
            membership_status: member.membership_status,
            joined_at: member.joined_at,
            roles: [{ id: member.role_key, name: member.role_label }],
          }));
          this.users_lists = this.users;
          this.loading = false;
        })
        .catch((error) => {
          console.log(error);
          this.loading = false;
        });
    },
    formatDate(dateString) {
      if (!dateString) return "Sin registro";

      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return "Sin registro";

      const formattedDate = date.toISOString().split("T")[0];
      let hours = date.getUTCHours();
      const minutes = String(date.getUTCMinutes()).padStart(2, "0");
      const isAM = hours < 12;
      if (hours > 12) hours -= 12;
      if (hours === 0) hours = 12;

      return `${formattedDate} ${hours}:${minutes}${isAM ? "am" : "pm"}`;
    },
    roleLabel(user) {
      return (
        user.role_label || user.roles?.[0]?.name || user.role_key || "Sin rol"
      );
    },
    isOwner(user) {
      return ["provider_owner", "concierge_owner"].includes(user.role_key);
    },
    membershipStatusLabel(status) {
      return status === "inactive" ? "Inactivo" : "Activo";
    },
    membershipBadgeClass(status) {
      return status === "inactive"
        ? "border-eblack-200 bg-eblack-100 text-eblack-600"
        : "border-egreen-200 bg-egreen-100 text-egreen-700";
    },
    applySearch() {
      const value = this.search.trim().toLowerCase();
      if (!value) {
        this.users_lists = this.users;
        return;
      }

      this.users_lists = this.users.filter((user) =>
        [
          user.assigned_person,
          user.email,
          user.dni,
          user.role_label,
          user.roles?.[0]?.name,
        ]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(value))
      );
    },
  },
  mounted() {
    this.initData();
  },
  computed: {
    isCurrentOrgAdmin() {
      // Org owners/admins are the roles granted OrganizationMember management.
      return (
        !!this.authStore.currentOrgId &&
        defineRulesFor(this.authStore.roleKeys, false).can(
          "manage",
          "OrganizationMember"
        )
      );
    },
  },
};
</script>
